import { format } from 'date-fns';

import type { SoScheduleEditResponse } from '@/services/donor-scheduling';
import { parseWallClock, toWallClockDate } from '@/utils';

const refId = (ref: SoScheduleEditResponse['donor']) => ref?.id ?? null;

const sameDate = (a?: string | null, b?: string | null) => {
  const left = parseWallClock(a);
  const right = parseWallClock(b);
  return (
    (left ? toWallClockDate(left) : null) ===
    (right ? toWallClockDate(right) : null)
  );
};

/**
 * Donor schedule fields to update after an SO schedule save. Mirrors the API
 * (SoScheduleEditService): only SO fields the user changed are copied, and a changed
 * owner goes to the billing account, never the donor schedule's account.
 */
export function donorSchedulePatchFromSoSchedule(
  saved: SoScheduleEditResponse,
  previous: SoScheduleEditResponse,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  if (refId(saved.donor) !== refId(previous.donor)) {
    patch.donorId = refId(saved.donor);
    patch.donor = saved.donor ?? null;
  }
  if (refId(saved.scheduleMaster) !== refId(previous.scheduleMaster)) {
    patch.scheduleMasterId = refId(saved.scheduleMaster);
    patch.scheduleMaster = saved.scheduleMaster ?? null;
  }
  if (!sameDate(saved.targetDate, previous.targetDate)) {
    const date = parseWallClock(saved.targetDate);
    patch.date = saved.targetDate ?? null;
    patch.dow = date ? format(date, 'eee') : null;
  }
  if ((saved.targetTime ?? null) !== (previous.targetTime ?? null)) {
    patch.targetTime = saved.targetTime ?? null;
  }
  if (refId(saved.site) !== refId(previous.site)) {
    patch.aspirationSiteId = refId(saved.site);
    patch.aspirationSite = saved.site ?? null;
  }
  if (refId(saved.fshDrug1) !== refId(previous.fshDrug1)) {
    patch.fshId = refId(saved.fshDrug1);
    patch.drug = saved.fshDrug1 ?? null;
  }
  if (refId(saved.fshDrug2) !== refId(previous.fshDrug2)) {
    patch.fsh2Id = refId(saved.fshDrug2);
    patch.drug2 = saved.fshDrug2 ?? null;
  }
  if ((saved.comment ?? null) !== (previous.comment ?? null)) {
    patch.billingComment = saved.comment ?? null;
  }
  if (
    (saved.clinicRanchHaulIn ?? null) !== (previous.clinicRanchHaulIn ?? null)
  ) {
    patch.clinicRanchHaulIn = saved.clinicRanchHaulIn ?? null;
  }
  if ((saved.billDrugs ?? null) !== (previous.billDrugs ?? null)) {
    patch.billDrugs = saved.billDrugs ?? null;
  }
  if ((saved.drugShipFee ?? null) !== (previous.drugShipFee ?? null)) {
    patch.drugShippingFee = saved.drugShipFee ?? null;
  }
  if (refId(saved.owner) !== refId(previous.owner)) {
    patch.billingAccountId = refId(saved.owner);
    patch.billingAccount = saved.owner ?? null;
  }

  return patch;
}
